import {
  INVESTIGATION_HISTORY_PANEL_SCROLLBAR_MIN_THUMB_HEIGHT_PX,
  INVESTIGATION_HISTORY_PANEL_SCROLLBAR_TRACK_INSET_PX,
} from "#src/routes/investigations/history/consts";
import { useCallback, useEffect, useLayoutEffect, useState } from "react";

type InvestigationHistoryPanelScrollbarMetricsType = {
  isScrollbarVisible: boolean;
  thumbHeightPx: number;
  thumbOffsetPx: number;
};

const DEFAULT_SCROLLBAR_METRICS: InvestigationHistoryPanelScrollbarMetricsType =
  {
    isScrollbarVisible: false,
    thumbHeightPx: 0,
    thumbOffsetPx: 0,
  };

const getInvestigationHistoryPanelScrollbarMetrics = ({
  scrollTop,
  scrollHeight,
  clientHeight,
}: {
  scrollTop: number;
  scrollHeight: number;
  clientHeight: number;
}): InvestigationHistoryPanelScrollbarMetricsType => {
  const scrollableDistancePx = scrollHeight - clientHeight;

  if (scrollableDistancePx <= 0 || clientHeight <= 0) {
    return DEFAULT_SCROLLBAR_METRICS;
  }

  const trackHeightPx = Math.max(
    clientHeight - INVESTIGATION_HISTORY_PANEL_SCROLLBAR_TRACK_INSET_PX * 2,
    INVESTIGATION_HISTORY_PANEL_SCROLLBAR_MIN_THUMB_HEIGHT_PX
  );
  const thumbHeightPx = Math.max(
    INVESTIGATION_HISTORY_PANEL_SCROLLBAR_MIN_THUMB_HEIGHT_PX,
    (clientHeight / scrollHeight) * trackHeightPx
  );
  const maxThumbOffsetPx = Math.max(trackHeightPx - thumbHeightPx, 0);
  const thumbOffsetPx = (scrollTop / scrollableDistancePx) * maxThumbOffsetPx;

  return {
    isScrollbarVisible: true,
    thumbHeightPx,
    thumbOffsetPx,
  };
};

export const useInvestigationHistoryPanelScrollbarOverlay = (
  scrollAreaElement: HTMLElement | null,
  remeasureDependency?: unknown
): InvestigationHistoryPanelScrollbarMetricsType => {
  const [scrollbarMetrics, setScrollbarMetrics] =
    useState<InvestigationHistoryPanelScrollbarMetricsType>(
      DEFAULT_SCROLLBAR_METRICS
    );

  const updateScrollbarMetrics = useCallback(() => {
    if (!scrollAreaElement) {
      setScrollbarMetrics(DEFAULT_SCROLLBAR_METRICS);
      return;
    }

    setScrollbarMetrics(
      getInvestigationHistoryPanelScrollbarMetrics({
        scrollTop: scrollAreaElement.scrollTop,
        scrollHeight: scrollAreaElement.scrollHeight,
        clientHeight: scrollAreaElement.clientHeight,
      })
    );
  }, [scrollAreaElement]);

  useLayoutEffect(() => {
    if (!scrollAreaElement) {
      setScrollbarMetrics(DEFAULT_SCROLLBAR_METRICS);
      return;
    }

    updateScrollbarMetrics();

    let animationFrameId = 0;
    let animationFrameCount = 0;

    const scheduleRemeasure = () => {
      updateScrollbarMetrics();

      if (animationFrameCount >= 4) {
        return;
      }

      animationFrameCount += 1;
      animationFrameId = window.requestAnimationFrame(scheduleRemeasure);
    };

    animationFrameId = window.requestAnimationFrame(scheduleRemeasure);

    return () => {
      window.cancelAnimationFrame(animationFrameId);
    };
  }, [remeasureDependency, scrollAreaElement, updateScrollbarMetrics]);

  useEffect(() => {
    if (!scrollAreaElement) {
      return;
    }

    scrollAreaElement.addEventListener("scroll", updateScrollbarMetrics, {
      passive: true,
    });

    const resizeObserver = new ResizeObserver(updateScrollbarMetrics);
    resizeObserver.observe(scrollAreaElement);

    const listRootElement = scrollAreaElement.firstElementChild;

    if (listRootElement instanceof HTMLElement) {
      resizeObserver.observe(listRootElement);

      const listElement = listRootElement.querySelector("ol");

      if (listElement instanceof HTMLElement) {
        resizeObserver.observe(listElement);
      }
    }

    const mutationObserver = new MutationObserver(updateScrollbarMetrics);
    mutationObserver.observe(scrollAreaElement, {
      attributes: true,
      childList: true,
      subtree: true,
    });

    return () => {
      scrollAreaElement.removeEventListener("scroll", updateScrollbarMetrics);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [remeasureDependency, scrollAreaElement, updateScrollbarMetrics]);

  return scrollbarMetrics;
};
